import {MoovementApiClient} from "src/app/infrastructure/moovement-api/web-api/moovement.api-client";
import {Injectable} from "@angular/core";
import {map, Observable} from "rxjs";
import {
  Animal,
  Pregnancy,
  WeightMeasurement,
  WeightMeasurementUnit
} from "src/app/infrastructure/moovement-api/models/asset";

interface RawWeightMeasurement {
  id: string;
  animalId: string;
  weight: number;
  unit: WeightMeasurementUnit,
  submittedAt: string
}

export interface AddWeightMeasurementRequest {
  weight: number;
  unit: WeightMeasurementUnit;
  submittedAt: Date;
}

export interface Relationship {
  parent: Animal,
  child: Animal
}

@Injectable({
  providedIn: 'root'
})
export class AnimalApiClient extends MoovementApiClient {
  removeRelationship(parent: string, child: string) {
    return this.httpClient.delete(`${this.baseUrl}/api/animal/relationships`, {
      body : {
        parent: parent,
        child: child
      }
    });
  }

  mergeAnimal(animal: Animal): Observable<Animal> {
    return this.httpClient.post<Animal>(`${this.baseUrl}/api/animal`, animal).pipe(
      map(enrichAnimal)
    );
  }

  setProfilePicture(animalId: string, blob: Blob): Observable<Animal> {
    return this.httpClient.post<Animal>(`${this.baseUrl}/api/animal/${animalId}/profilePicture`, blob).pipe(
      map(enrichAnimal)
    );
  }

  updatePregnancyStatus(animalId: string, pregnancyStatus: Pregnancy): Observable<Pregnancy> {
    return this.httpClient.put<Pregnancy>(`${this.baseUrl}/api/animal/${animalId}/pregnancies/${pregnancyStatus.id}`, pregnancyStatus);
  }

  addPregnancyStatus(animalId: string, pregnancyStatus: Pregnancy) {
    return this.httpClient.post<Pregnancy>(`${this.baseUrl}/api/animal/${animalId}/pregnancies`, pregnancyStatus);
  }

  deleteProfilePicture(animalId: string): Observable<Animal> {
    return this.httpClient.delete<Animal>(`${this.baseUrl}/api/animal/${animalId}/profilePicture`).pipe(
      map(enrichAnimal)
    );
  }

  getParents(animalId: string): Observable<Animal> {
    return this.httpClient.get<Animal>(`${this.baseUrl}/api/animal/${animalId}/ancestors?generations=1`).pipe(
      map(enrichAnimal)
    );
  }

  getChildren(animalId: string): Observable<Animal> {
    return this.httpClient.get<Animal>(`${this.baseUrl}/api/animal/${animalId}/descendants?generations=1`).pipe(
      map(enrichAnimal)
    );
  }

  clearAssignedPaddock(id: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/api/animal/${id}/paddock`);
  }

  getAnimal(id: string) {
    return this.httpClient.get<Animal>(`${this.baseUrl}/api/animal/${id}`).pipe(
      map(enrichAnimal)
    );
  }

  addWeightMeasurement(id: string, body: AddWeightMeasurementRequest) {
    return this.httpClient.post<WeightMeasurement>(`${this.baseUrl}/api/animal/${id}/weight`, body);
  }

  getPregnancyStatus(id: string): Observable<Pregnancy[]> {
    return this.httpClient.get<Pregnancy[]>(`${this.baseUrl}/api/animal/${id}/pregnancies`)
  }

  getWeightMeasurements(id: string): Observable<WeightMeasurement[]> {
    return this.httpClient.get<RawWeightMeasurement[]>(`${this.baseUrl}/api/animal/${id}/weight`)
    .pipe(map(measurements => measurements.map(rawMeasurement => ({
      id: rawMeasurement.id,
      animalId: rawMeasurement.animalId,
      weight: rawMeasurement.weight,
      unit: rawMeasurement.unit,
      submittedAt: new Date(rawMeasurement.submittedAt)
    } as WeightMeasurement))));
  }

  deletePregnancy(animalId: string, id: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/api/animal/${animalId}/pregnancies/${id}`)
  }

  registerParentChildRelationship(child: string, parent: string) {
    return this.httpClient.post<Relationship>(`${this.baseUrl}/api/animal/relationships`, {
      parent: parent,
      child: child
    })
  }

  getAvailableRelationships(id: string) {
    return this.httpClient.get<Animal[]>(`${this.baseUrl}/api/animal/${id}/relationships`)
  }
}

export const enrichAnimal = (animal: Animal) => {
  animal.name = animal.visualTag;
  return animal;
}
